import type { Currency, Locale, Plan, BillingInterval, PlanDefinition, PlanPricing } from "./types.js";

/**
 * Source of truth for plan pricing.
 * Amounts are in cents. BRL is the default (pt-BR); USD applies to en-US / es-ES.
 */
export const PLANS: Record<Plan, PlanDefinition> = {
  basic: {
    id: "basic",
    monthlyCents: 3700, // R$ 37/mês
    annualCents: 30000, // R$ 300/ano
    usdMonthlyCents: 700, // US$ 7/mês
    usdAnnualCents: 6000, // US$ 60/ano
    features: [
      "Crie conteúdo para 1 conta do Instagram",
      "Publique até 30 posts de imagem no feed por mês",
      "Publique até 30 stories por mês",
      "Publique até 30 carrosséis por mês",
      "Planejador de conteúdo que gera conteúdo de um mês em 15 minutos",
      "Integração com Instagram homologada pela Meta",
      "Agendamento inteligente com publicação automática",
      "Garantia de satisfação ou dinheiro de volta",
    ],
  },
  premium: {
    id: "premium",
    monthlyCents: 5700, // R$ 57/mês
    annualCents: 42000, // R$ 420/ano
    usdMonthlyCents: 1200, // US$ 12/mês
    usdAnnualCents: 8000, // US$ 80/ano
    features: [
      "Crie conteúdo para 1 conta do Instagram",
      "Publique posts ilimitados",
      "Publique stories ilimitados",
      "Publique carrosséis ilimitados",
      "Planejador de conteúdo que gera conteúdo de um mês em 15 minutos",
      "Integração com Instagram homologada pela Meta",
      "Agendamento inteligente com publicação automática",
      "Garantia de satisfação ou dinheiro de volta",
      "Publique até 20 reels, vídeos curtos prontos para postar",
      // Temporarily hidden — bring back with advanced formats in a few months.
      // "Story carrossel, sequência de stories em uma única peça",
      "Story com música",
      "Story com vídeo e trilha sonora integrada",
      "Post com música",
      "Carrossel com música",
      // Temporarily hidden — feature not shipping yet.
      // "Agente WhatsApp, receba os conteúdos prontos no WhatsApp e aprove com um toque",
    ],
  },
};

const USD_LOCALES = new Set<string>(["en-US", "es-ES"]);

/** Map app language → charge/display currency. */
export function currencyFromLocale(locale: string | null | undefined): Currency {
  const normalized = normalizeLocale(locale);
  return USD_LOCALES.has(normalized) ? "USD" : "BRL";
}

export function normalizeLocale(locale: string | null | undefined): Locale {
  if (!locale) return "pt-BR";
  // WhatsApp / marketing links sometimes use underscores (pt_BR).
  const normalized = locale.replace(/_/g, "-");
  if (normalized === "en-US" || normalized === "es-ES" || normalized === "pt-BR") return normalized;
  const lower = normalized.toLowerCase();
  if (lower === "en-us" || lower.startsWith("en")) return "en-US";
  if (lower === "es-es" || lower.startsWith("es")) return "es-ES";
  if (lower === "pt-br" || lower.startsWith("pt")) return "pt-BR";
  return "pt-BR";
}

/** pt-BR checkout market (BRL pricing). CPF may be required for Brazilian phones. */
export function isBrazilCheckoutLocale(locale: string | null | undefined): boolean {
  return currencyFromLocale(locale) === "BRL";
}

/** PIX / OpenPix is Brazil + pt-BR only. */
export function isPixAvailableForLocale(locale: string | null | undefined): boolean {
  return isBrazilCheckoutLocale(locale);
}

function planCents(plan: Plan, currency: Currency): { monthly: number; annual: number } {
  const def = PLANS[plan];
  if (currency === "USD") {
    return { monthly: def.usdMonthlyCents, annual: def.usdAnnualCents };
  }
  return { monthly: def.monthlyCents, annual: def.annualCents };
}

export function getPlanPricing(
  plan: Plan,
  interval: BillingInterval,
  currency: Currency = "BRL",
): PlanPricing {
  const cents = planCents(plan, currency);
  if (interval === "monthly") {
    return {
      plan,
      interval,
      totalCents: cents.monthly,
      monthlyEquivalentCents: cents.monthly,
    };
  }
  return {
    plan,
    interval,
    totalCents: cents.annual,
    monthlyEquivalentCents: Math.floor(cents.annual / 12),
  };
}

/**
 * Annual savings compared to paying monthly for 12 months, for the same plan.
 */
export function getAnnualSavings(
  plan: Plan,
  currency: Currency = "BRL",
): { savingsCents: number; percentage: number } {
  const cents = planCents(plan, currency);
  const monthlyForYear = cents.monthly * 12;
  const savingsCents = monthlyForYear - cents.annual;
  const percentage = monthlyForYear === 0 ? 0 : Math.round((savingsCents / monthlyForYear) * 100);
  return { savingsCents, percentage };
}

/**
 * Annual installments for credit card. We always display 12x; the actual provider
 * commitment is implemented at the Stripe layer (with safe fallback to upfront).
 */
export function getAnnualInstallmentCount(): number {
  return 12;
}

export function getAnnualInstallmentCents(plan: Plan, currency: Currency = "BRL"): number {
  return Math.floor(planCents(plan, currency).annual / 12);
}
